"use client"

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { apiClient } from './api-client'

// ============================================================================
// Types
// ============================================================================

export interface User {
  id: string
  username: string
  email?: string
  createdAt: string
  lastLoginAt?: string
}

export interface UserPermissions {
  roles: string[]
  isRootAdmin: boolean
  isSiteAdmin: boolean
  isNewsEditor: boolean
  isServerAdmin: boolean
  isCommitteeEditor: boolean
  isDocsMaintainer: boolean
  isReadOnlyStaff: boolean
  hasAnyAdminRole: boolean
  serverAdminFor: string[]
  committeeEditorFor: string[]
}

export interface AuthState {
  user: User | null
  permissions: UserPermissions | null
  isLoading: boolean
  isAuthenticated: boolean
  isLoggingOut: boolean
}

export interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<LoginResult>
  logout: () => Promise<void>
  refresh: () => Promise<boolean>
  checkAuth: () => Promise<void>
}

export interface LoginCredentials {
  username: string
  password: string
  totp?: string
  rememberMe?: boolean
  stateToken?: string
}

export interface LoginResult {
  success: boolean
  mfaRequired?: boolean
  stateToken?: string
  expiresAt?: string
  error?: string
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// ============================================================================
// Provider Component
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    permissions: null,
    isLoading: true,
    isAuthenticated: false,
    isLoggingOut: false,
  })

  const router = useRouter()
  const hasInitialized = useRef(false)

  // ============================================================================
  // Auth Methods
  // ============================================================================

  const checkAuth = useCallback(async (): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true }))

    const result = await apiClient.get<{ user: User; permissions: UserPermissions }>('/api/auth/user')

    if (!result.success) {
      // Don't log 401 errors as they're expected when not authenticated
      if (!result.error.error.includes('unauthorized')) {
        console.error('Auth check failed:', result.error)
      }
      
      setState({
        user: null,
        permissions: null,
        isLoading: false,
        isAuthenticated: false,
        isLoggingOut: false,
      })
      return
    }

    setState({
      user: result.data.user,
      permissions: result.data.permissions,
      isLoading: false,
      isAuthenticated: true,
      isLoggingOut: false,
    })
  }, [])

  const login = useCallback(async (credentials: LoginCredentials): Promise<LoginResult> => {
    setState(prev => ({ ...prev, isLoading: true }))

    const result = await apiClient.post<{ 
      user?: User; 
      mfa_required?: boolean;
      state_token?: string;
      expires_at?: string;
      message?: string;
    }>('/api/auth/login', credentials)

    if (!result.success) {
      setState(prev => ({ ...prev, isLoading: false }))
      return { success: false, error: result.error.message }
    }

    if (result.data.mfa_required) {
      setState(prev => ({ ...prev, isLoading: false }))
      return { 
        success: false, 
        mfaRequired: true,
        stateToken: result.data.state_token,
        expiresAt: result.data.expires_at
      }
    }

    // Add a small delay to ensure cookies are set before fetching user permissions
    await new Promise(resolve => setTimeout(resolve, 100))
    
    // Fetch user permissions after successful login
    await checkAuth()
    return { success: true }
  }, [checkAuth])

  const logout = useCallback(async (): Promise<void> => {
    setState(prev => ({ ...prev, isLoggingOut: true }))

    // Call logout API
    await apiClient.post('/api/auth/logout')

    // Small delay to show the logging out message
    await new Promise(resolve => setTimeout(resolve, 1500))

    // Clear state
    setState({
      user: null,
      permissions: null,
      isLoading: false,
      isAuthenticated: false,
      isLoggingOut: false,
    })

    // Redirect to home
    router.push('/')
  }, [router])

  const refresh = useCallback(async (): Promise<boolean> => {
    const result = await apiClient.post<{ user: User }>('/api/auth/refresh')

    if (!result.success) {
      // Clear state on refresh failure
      setState({
        user: null,
        permissions: null,
        isLoading: false,
        isAuthenticated: false,
        isLoggingOut: false,
      })
      return false
    }

    // Fetch updated user info
    await checkAuth()
    return true
  }, [checkAuth])

  // ============================================================================
  // Auto Refresh Logic
  // ============================================================================

  useEffect(() => {
    // Check auth status on mount only once
    if (!hasInitialized.current) {
      hasInitialized.current = true
      checkAuth()
    }

    // Set up auto-refresh timer (every 10 minutes)
    if (!state.isAuthenticated) return

    const interval = setInterval(() => {
      refresh()
    }, 10 * 60 * 1000)

    return () => clearInterval(interval)
  }, [state.isAuthenticated, refresh, checkAuth])

  // ============================================================================
  // Context Value
  // ============================================================================

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    refresh,
    checkAuth,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      
      {/* Logout Loading Overlay */}
      {state.isLoggingOut && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-card border rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center space-y-6">
              <div className="w-16 h-16 rounded-full theme-btn-gradient flex items-center justify-center mx-auto shadow-lg">
                <svg className="h-8 w-8 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-foreground">Signing out...</h3>
                <p className="text-muted-foreground">Please wait while we securely sign you out</p>
              </div>
              <div className="flex justify-center">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
                  <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  )
}

// ============================================================================
// Hook
// ============================================================================

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// ============================================================================
// Convenience Hooks
// ============================================================================

export function useUser(): User | null {
  const { user } = useAuth()
  return user
}

export function usePermissions(): UserPermissions | null {
  const { permissions } = useAuth()
  return permissions
}

export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuth()
  return isAuthenticated
}

export function useIsLoading(): boolean {
  const { isLoading } = useAuth()
  return isLoading
}

export function useHasRole(role: string): boolean {
  const { permissions } = useAuth()
  if (!permissions) return false
  
  // Direct role check
  if (permissions.roles.includes(role)) {
    return true
  }
  
  // Role hierarchy checks
  if (permissions.isRootAdmin) {
    return true // RootAdmin has all roles
  }
  
  if (permissions.isSiteAdmin) {
    // SiteAdmin has all roles except RootAdmin
    return role !== 'RootAdmin'
  }
  
  return false
}

export function useHasAdminRole(): boolean {
  const { permissions } = useAuth()
  return permissions?.hasAnyAdminRole ?? false
}

export function useHasServerAdmin(serverId?: string): boolean {
  const { permissions } = useAuth()
  if (!permissions) return false
  if (permissions.isRootAdmin || permissions.isSiteAdmin) return true
  if (!serverId) return permissions.isServerAdmin
  return permissions.serverAdminFor.includes(serverId)
}

export function useHasCommitteeEdit(committeeId?: string): boolean {
  const { permissions } = useAuth()
  if (!permissions) return false
  if (permissions.isRootAdmin || permissions.isSiteAdmin) return true
  if (!committeeId) return permissions.isCommitteeEditor
  return permissions.committeeEditorFor.includes(committeeId)
}

// ============================================================================
// HOCs and Components
// ============================================================================

interface RequireAuthProps {
  children: React.ReactNode
  fallback?: React.ReactNode
  requireAdmin?: boolean
  requiredRole?: string
}

export function RequireAuth({ 
  children, 
  fallback, 
  requireAdmin = false, 
  requiredRole 
}: RequireAuthProps) {
  const { isAuthenticated, isLoading, permissions } = useAuth()

  if (isLoading) {
    return <div className="flex items-center justify-center p-8">Loading...</div>
  }

  if (!isAuthenticated) {
    return fallback || <div>Please log in to access this content.</div>
  }

  if (requireAdmin && !permissions?.hasAnyAdminRole) {
    return fallback || <div>Admin access required.</div>
  }

  if (requiredRole && !permissions?.roles.includes(requiredRole)) {
    return fallback || <div>Insufficient permissions.</div>
  }

  return <>{children}</>
}

export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
  options: {
    requireAdmin?: boolean
    requiredRole?: string
    fallback?: React.ReactNode
  } = {}
) {
  return function AuthenticatedComponent(props: P) {
    return (
      <RequireAuth {...options}>
        <Component {...props} />
      </RequireAuth>
    )
  }
}